import axios from "axios";
import { HttpInnerResponse } from "../models/HttpInnerResponse";
import { Importeduser } from "../models/ImportedUser";
import { IAutocompleteUser } from "../models/AutocompleteUser";
import { FileUtilService, ISheetData } from "./util/FileUtilService";
import { Endpoints } from '../config/endpoints';
import { ICustomer } from '../models/Customer'
import { IUser } from "../models/User";
import { IAccessLevel } from "../models/AccessLevel";
import { EncryptHelper } from "helpers/EncryptHelper";
import { IUserPasswordValidationsResponse } from "models/UserPasswordValidations";

interface IUsersService {
  getServiceEndpoint(): string;
  getUsers(query: string): Promise<IUser[]>;
  getUsersByTenant(tenantId: string): Promise<IUser[]>;
  getAppUsersByRegion(regionId: number): Promise<any[]>;
  getUserById(userId: string): Promise<Pick<IAutocompleteUser, 'id' | 'code'>>;
  getAllUsers(): Promise<IUser[]>;
  getAccessLevels(): Promise<IAccessLevel[]>;
  createUsersUsingFile(file: File): Promise<any>;
  getCustomers(query?: string): Promise<ICustomer[]>;
  createUser(user: IUser): Promise<void>;
  saveUser(user: IUser): Promise<void>;
  updateUserFields(userId: string, fields: Object): Promise<void>;
  deleteUser(user: IUser): Promise<void>;
  blockUser(userName: string): Promise<any>;
  activateUser(userId: string): Promise<void>;
  getUserPasswordValidations(userId: string): Promise<IUserPasswordValidationsResponse>;
  getAppUsers(params: { id: string }): Promise<IUser[]>;
  getUserPreferences(): Promise<any[]>;
  updateUserPreferences(preferences: Object): Promise<void>;
}

export const UsersService: IUsersService = {
  getServiceEndpoint: (): string => {
    return Endpoints.getEndpoint("REACT_APP_IDENTITY_ENDPOINT");
  },

  async createUsersUsingFile(file: File): Promise<any> {
    const { data }: ISheetData = await FileUtilService.readExcelFile(file);
    const promises: Promise<{ status: string; data: any }>[] = data.map(
      async (row: Importeduser) =>
        await new Promise(async (resolve, reject) => {
          try {
            const userEndpoint = `${UsersService.getServiceEndpoint()}user/`;
            const { data } = await axios.post<HttpInnerResponse>(`${userEndpoint}/add`, row);
            resolve({ status: "resolved", data });
          } catch (e) {
            reject({ status: "rejected", data });
          }
        })
    );
    return promises;
  },

  getUsers: async (criteria: string): Promise<IUser[]> => {
    // TODO: Update according to real service query params syntax
    const params = {
      q: criteria,
    };
    const url = `${UsersService.getServiceEndpoint()}users`;
    const { data } = await axios.post(url, { params });
    return data;
  },

  getUsersByTenant: async (tenantId: string): Promise<IUser[]> => {
    const userEndpoint = `${UsersService.getServiceEndpoint()}user/`;
    const url = `${userEndpoint}tenant/${tenantId}`;
    const { data } = await axios.get(`${url}`);
    return Promise.resolve(data);
  },

  getAppUsersByRegion: async (regionId: number): Promise<IUser[]> => {
    const appUserEndpoint = `${UsersService.getServiceEndpoint()}userApp/`;
    const url = `${appUserEndpoint}region/${regionId}`;
    const { data } = await axios.get(`${url}`);
    return Promise.resolve(data);
  },

  getAppUsers: async (params: { id: string }): Promise<IUser[]> => {
    const appUserEndpoint = `${UsersService.getServiceEndpoint()}userApp/`;
    const url = `${appUserEndpoint}getUsers`;
    const { data } = await axios.get(`${url}`, {params});
    return Promise.resolve(data);
  },

  getAccessLevels: async (): Promise<IAccessLevel[]> => {
    const identityEndpoint = UsersService.getServiceEndpoint();
    const url = `${identityEndpoint}accessLevel/`;
    const { data } = await axios.get(url);
    return data;
  },

  getAllUsers: async (): Promise<IUser[]> => {
    const url = `${UsersService.getServiceEndpoint()}user/all`;
    const { data } = await axios.post(url);
    return data;
  },

  getUserById: async (userId: string): Promise<{ id: string, code: string }> => {
    const url = `${UsersService.getServiceEndpoint()}user/${userId}`;
    const { data } = await axios.get(url);
    return data;
  },

  getCustomers: async (criteria: string): Promise<ICustomer[]> => {
    const params = {
      q: criteria || ''
    };
    const url = `${UsersService.getServiceEndpoint()}customers`;
    const { data } = await axios.get(url, { params });
    return data;
  },

  createUser: async (user: IUser): Promise<void> => {
    const identityEndpoint = UsersService.getServiceEndpoint();
    const { data } = await axios.post(`${identityEndpoint}signup`, user);
    return Promise.resolve(data);
  },

  saveUser: async (user: IUser): Promise<void> => {
    const userEndpoint = `${UsersService.getServiceEndpoint()}user/`;
    return axios.put(`${userEndpoint}`, user);
  },

  updateUserFields: async (userId: string, fields: Object): Promise<void> => {
    const userEndpoint = `${UsersService.getServiceEndpoint()}user/`;
    return axios.put(`${userEndpoint}update/${userId}`, fields);
  },

  deleteUser: async (user: IUser): Promise<void> => {
    const userEndpoint = `${UsersService.getServiceEndpoint()}user/`;
    return axios.delete(`${userEndpoint}${user.id}/${user.tenantId}`,);
  },

  blockUser: async (userName: string): Promise<any> => {
    const userEndpoint = `${UsersService.getServiceEndpoint()}user/`;
    const payload = JSON.stringify({
      login: userName
    })
    const message = EncryptHelper.encrypt(payload);
    return axios.put(`${userEndpoint}block`, { message });
  },

  activateUser: async (userId: string): Promise<void> => {
    const identityEndpoint = UsersService.getServiceEndpoint();
    return axios.post(`${identityEndpoint}signup/activate/${userId}`);
  },

  getUserPasswordValidations: async (userId: string): Promise<IUserPasswordValidationsResponse> => {
    const userEndpoint = `${UsersService.getServiceEndpoint()}user/`;
    const encryptedUserId = EncryptHelper.encrypt(userId)
    const { data } = await axios.get(`${userEndpoint}passwordValidations/${encryptedUserId}`);
    return data;
  },

  getUserPreferences: async (): Promise<any[]> => {
    const userPreferencesEndpoint = `${UsersService.getServiceEndpoint()}user/preferences/`;
    const { data } = await axios.get(userPreferencesEndpoint);
    return data;
  },

  updateUserPreferences: async (preferences: Object): Promise<void> => {
    const userPreferencesEndpoint = `${UsersService.getServiceEndpoint()}user/preferences/`;
    return axios.put(`${userPreferencesEndpoint}`, preferences);
  }

} as IUsersService;
 